import { DEFAULT_PAGE_SIZE } from "@/shared/constants";
import config from "@shared/config/client";
import queryString from "query-string";
import { ICallableRequestBuilder } from "../requestBuilder/types";
import { FeedPostDto, FeedPostDtoWithOgMetadata } from "./types";
import * as linkifyjs from "linkifyjs";
import { asyncMap } from "@/shared/array";

export const getGlobalFeedPosts =
  (request: ICallableRequestBuilder<FeedPostDto[]>) =>
  async (take = DEFAULT_PAGE_SIZE, skip = 0) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/posts`,
      query: {
        isMyFeed: false,
        take: take,
        skip: skip,
      },
    });

    return request.call(url);
  };

export const getMyFeedPosts =
  (request: ICallableRequestBuilder<FeedPostDto[]>) =>
  async (take = DEFAULT_PAGE_SIZE, skip = 0) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/posts`,
      query: {
        isMyFeed: true,
        take: take,
        skip: skip,
      },
    });

    return request.call(url);
  };

export const getNewsFeedPosts =
  (request: ICallableRequestBuilder<FeedPostDto[]>) =>
  async (take = DEFAULT_PAGE_SIZE, skip = 0) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/posts`,
      query: {
        isMyFeed: false,
        isNewsFeed: true,
        take: take,
        skip: skip,
      },
    });

    return request.call(url);
  };

export const enrichWithOgMetadata = async (posts: FeedPostDto[]) => {
  const enrichedPostsPromises = posts.map(async (post) => {
    let postOgData = null;
    if (
      !post.attachedNftId &&
      !post.gallery?.length &&
      !post.repostId &&
      post.text
    ) {
      const link = linkifyjs
        .find(post.text)
        .find(
          (res: any) =>
            res.type === "url" && !post.ignoredLinks.includes(res.href),
        );

      if (link) {
        const ogDataResult = await fetch(
          `${config.url}/api/ogs?url=${link.href}`,
        );
        postOgData = await ogDataResult.json();
      }
    }
    return { ...post, ogMetadata: postOgData } as FeedPostDtoWithOgMetadata;
  });

  return await Promise.all(enrichedPostsPromises);
};

export const enrichWithOgImage = async (posts: FeedPostDto[]) => {
  return await asyncMap(posts, async (post) => {
    if (post.gallery.length) {
      return post;
    }

    const result = await fetch(
      `${config.url}/api/ogs?url=${post.portalMetadata.source}`,
    );

    const data = await result.json();

    if (!data) {
      return post;
    }

    return {
      ...post,
      gallery: [
        {
          imageUrl: data.image,
          postId: post.id,
        },
      ],
    };
  });
};

export const getGlobalPost =
  (request: ICallableRequestBuilder<FeedPostDto>) => async (postId: string) => {
    return request.call(`${config.apiUrl}/posts/${postId}`);
  };

const requests = {
  getGlobalFeedPosts,
  getMyFeedPosts,
  getNewsFeedPosts,
  getGlobalPost,
  enrichWithOgMetadata,
  enrichWithOgImage,
};

export default requests;
